import { useCallback, useContext, useEffect, useState } from "react";
import { useLineageContext, useLineageDispatch } from "../context/LineageProvider";
import { DEFAULT_MIN_ZOOM } from "../utils";
import {
  calculateMinLevel,
  calculateNodeCount,
  expandTableLineageLevelWise,
  highlightTableConnections,
  layoutElementsOnCanvas,
  pruneToLevelBand,
} from "../graph";
import { Button } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import {
  setLeftExpansion,
  setMinRange,
  setNodeCount,
  setRightExpansion,
} from "../context/LineageSlice";
import { useReactFlow } from "reactflow";
import { BetterPopover, PopoverContext } from "./BetterPopover";
import { ExpandIcon } from "../assets/icons";
import ExpansionLevelControls from "./ExpansionLevelControls";

const AutoExpansionPopover = () => {
  const flow = useReactFlow();
  const {
    state: { selectedTable, leftExpansion, rightExpansion, minRange, nodeCount },
    rerender,
  } = useLineageContext();
  const [maxRange, setMaxRange] = useState<[number, number]>([0, 0]);
  // levels currently rendered on canvas relative to the root, [parents, children]
  const [currentExpansion, setCurrentExpansion] = useState<[number, number]>([0, 0]);
  const lineageDispatch = useLineageDispatch();

  const { openedPopover } = useContext(PopoverContext);
  const popoverId = "auto-expansion-popover";
  const isPopoverOpen = openedPopover === popoverId;

  useEffect(() => {
    (async () => {
      lineageDispatch(
        setNodeCount(
          await calculateNodeCount(
            flow.getNodes(),
            flow.getEdges(),
            selectedTable,
            leftExpansion,
            rightExpansion
          )
        )
      );
    })();
  }, [flow, leftExpansion, lineageDispatch, rightExpansion, selectedTable]);

  useEffect(() => {
    (async () => {
      if (!isPopoverOpen) return;
      if (!selectedTable) return;
      const selectedTableData = flow.getNode(selectedTable)?.data;
      if (!selectedTableData) return;
      const { level } = selectedTableData;
      const nodes = flow.getNodes();
      const edges = flow.getEdges();

      // how far the graph is already expanded in each direction, so the
      // popover can offer to collapse back down (not just add more)
      let curMinLevel = level;
      let curMaxLevel = level;
      for (const n of nodes) {
        // column / see-more / operator nodes have no level — skip them so the
        // min/max (and the resulting stepper values) don't become NaN
        if (!Number.isFinite(n.data.level)) continue;
        curMinLevel = Math.min(curMinLevel, n.data.level);
        curMaxLevel = Math.max(curMaxLevel, n.data.level);
      }
      const curLeft = level - curMinLevel;
      const curRight = curMaxLevel - level;
      setCurrentExpansion([curLeft, curRight]);
      // Sync the steppers to what is actually on screen so reopening the popover
      // reflects the current graph instead of the last applied / default values.
      lineageDispatch(setLeftExpansion(curLeft));
      lineageDispatch(setRightExpansion(curRight));

      const [newNodes] = await expandTableLineageLevelWise(
        nodes,
        edges,
        selectedTable,
        -Infinity,
        Infinity,
        Infinity
      );
      let minLevel = Infinity;
      let maxLevel = -Infinity;
      for (const n of newNodes) {
        if (!Number.isFinite(n.data.level)) continue;
        minLevel = Math.min(minLevel, n.data.level);
        maxLevel = Math.max(maxLevel, n.data.level);
      }
      setMaxRange([level - minLevel, maxLevel - level]);
    })();
  }, [flow, selectedTable, isPopoverOpen, lineageDispatch]);

  // Each stepper guards on `selectedTable` internally so the callbacks keep
  // stable identities (an external `guard(...)` wrapper would allocate a new
  // function every render and defeat these useCallback memoizations).
  const updateLeftExpansion = useCallback(() => {
    if (!selectedTable) return;
    const next = leftExpansion + 1 <= maxRange[0] ? leftExpansion + 1 : leftExpansion;
    lineageDispatch(setLeftExpansion(next));
  }, [selectedTable, leftExpansion, lineageDispatch, maxRange]);

  const updateRightExpansion = useCallback(() => {
    if (!selectedTable) return;
    const next = rightExpansion + 1 <= maxRange[1] ? rightExpansion + 1 : rightExpansion;
    lineageDispatch(setRightExpansion(next));
  }, [selectedTable, rightExpansion, lineageDispatch, maxRange]);

  const decrementLeftExpansion = useCallback(() => {
    if (!selectedTable) return;
    lineageDispatch(setLeftExpansion(Math.max(0, leftExpansion - 1)));
  }, [selectedTable, leftExpansion, lineageDispatch]);

  const decrementRightExpansion = useCallback(() => {
    if (!selectedTable) return;
    lineageDispatch(setRightExpansion(Math.max(0, rightExpansion - 1)));
  }, [selectedTable, rightExpansion, lineageDispatch]);

  const maxLeftExpansion = useCallback(() => {
    if (!selectedTable) return;
    lineageDispatch(setLeftExpansion(maxRange[0]));
  }, [selectedTable, lineageDispatch, maxRange]);

  const maxRightExpansion = useCallback(() => {
    if (!selectedTable) return;
    lineageDispatch(setRightExpansion(maxRange[1]));
  }, [selectedTable, lineageDispatch, maxRange]);

  // Apply removes (collapses) when the chosen band is smaller than what is
  // already on screen; otherwise it only adds.
  const willShrink = leftExpansion < currentExpansion[0] || rightExpansion < currentExpansion[1];

  return (
    <BetterPopover
      id={popoverId}
      trigger={
        <Button variant="outline" size="xs" type="button">
          <ExpandIcon />
          Depth
        </Button>
      }
      render={({ close }) => (
        <Stack direction="column" gap="xs">
          <ExpansionLevelControls
            leftExpansion={leftExpansion}
            rightExpansion={rightExpansion}
            minRange={minRange}
            onMaxLeft={maxLeftExpansion}
            onExpandLeft={updateLeftExpansion}
            onCollapseLeft={decrementLeftExpansion}
            onCollapseRight={decrementRightExpansion}
            onExpandRight={updateRightExpansion}
            onMaxRight={maxRightExpansion}
          />
          <Stack direction="row" className="al-w-full" gap="xl" justify="between" align="center">
            <div className="normal-text">Parents</div>
            <div className="normal-text">Children</div>
          </Stack>
          <Button
            variant={nodeCount === 0 && !willShrink ? "secondary" : "default"}
            size="sm"
            disabled={nodeCount === 0 && !willShrink}
            onClick={async e => {
              e.stopPropagation();
              if (!selectedTable) return;
              const selectedTableData = flow.getNode(selectedTable)?.data;
              if (!selectedTableData) return;
              const { level } = selectedTableData;
              let [nodes, edges] = await expandTableLineageLevelWise(
                flow.getNodes(),
                flow.getEdges(),
                selectedTable,
                level - leftExpansion,
                level + rightExpansion,
                Infinity
              );
              // collapse anything currently expanded beyond the chosen band
              [nodes, edges] = pruneToLevelBand(
                nodes,
                edges,
                selectedTable,
                level - leftExpansion,
                level + rightExpansion
              );
              [nodes, edges] = highlightTableConnections(nodes, edges, selectedTable);
              layoutElementsOnCanvas(nodes, edges);
              flow.setNodes(nodes);
              flow.setEdges(edges);
              flow.fitView({ minZoom: DEFAULT_MIN_ZOOM });
              lineageDispatch(setMinRange(calculateMinLevel(nodes, edges, selectedTable)));
              lineageDispatch(
                setNodeCount(
                  await calculateNodeCount(
                    nodes,
                    edges,
                    selectedTable,
                    leftExpansion,
                    rightExpansion
                  )
                )
              );
              rerender();
              close();
            }}
          >
            Apply
          </Button>
        </Stack>
      )}
    />
  );
};

export default AutoExpansionPopover;
